import { useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ArrowRight, Phone } from "lucide-react";
import { useShop } from "@/shop/ShopContext";

const CartDrawer = () => {
  const { cart, setQty, total, count, config } = useShop();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="cart-trigger-btn"
          className="relative flex items-center gap-2 rounded-full bg-[#FF5500] px-4 py-2 font-display text-sm uppercase tracking-wider text-white transition-transform hover:scale-105"
        >
          <ShoppingCart className="h-4 w-4" />
          <span className="hidden sm:inline">Carrello</span>
          {count > 0 && (
            <span data-testid="cart-count-badge" className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[11px] font-bold text-[#0C0E12]">
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="flex w-full flex-col border-l border-white/10 bg-[#14171D] text-neutral-100 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-xl uppercase tracking-widest text-neutral-100">Il tuo carrello</SheetTitle>
        </SheetHeader>
        {cart.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
            <ShoppingCart className="h-12 w-12 text-neutral-600" />
            <p className="text-neutral-400">Il carrello è vuoto.</p>
            <a href={config.phone_href} className="flex items-center gap-2 font-display text-sm uppercase tracking-wider text-[#FF5500]" data-testid="cart-empty-phone">
              <Phone className="h-4 w-4" /> Serve aiuto? {config.phone}
            </a>
          </div>
        ) : (
          <>
            <div className="mt-4 flex-1 space-y-4 overflow-y-auto pr-1">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-3 rounded-lg border border-white/10 bg-[#1C2028] p-3" data-testid={`cart-item-${item.id}`}>
                  <img src={item.image} alt={item.name} className="h-16 w-16 rounded-md object-cover" />
                  <div className="flex flex-1 flex-col">
                    <span className="text-sm font-semibold leading-tight">{item.name}</span>
                    <span className="mt-0.5 font-mono text-[11px] text-[#FF5500]">{item.code}</span>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(item.id, item.qty - 1)} className="rounded-md border border-white/15 p-1 hover:border-[#FF5500]" data-testid={`qty-minus-${item.id}`} aria-label="Diminuisci">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center font-mono text-sm" data-testid={`qty-value-${item.id}`}>{item.qty}</span>
                        <button onClick={() => setQty(item.id, item.qty + 1)} className="rounded-md border border-white/15 p-1 hover:border-[#FF5500]" data-testid={`qty-plus-${item.id}`} aria-label="Aumenta">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="font-display text-lg">€ {(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => setQty(item.id, 0)} className="self-start text-neutral-500 hover:text-red-400" data-testid={`remove-item-${item.id}`} aria-label="Rimuovi">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm uppercase tracking-widest text-neutral-400">Totale</span>
                <span className="font-display text-3xl text-[#FF5500]" data-testid="cart-total">€ {total.toFixed(2)}</span>
              </div>
              <Link
                to="/checkout"
                onClick={() => setOpen(false)}
                data-testid="checkout-btn"
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#FF5500] py-3.5 font-display text-base uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
              >
                Vai al checkout <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-3 text-center text-xs text-neutral-500">
                Pagamento con carta o bonifico · Info: {config.phone}
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
